import { useState } from "react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityName } from "@/components/cinch/EntityName";
import { StatusBadge } from "@/components/cinch/StatusBadge";

import {
  severityBadgeValue,
  useChildren,
  useIncidents,
  useNewEntities,
  useProfiles,
  type ChildRow,
  type FirstSeenOut,
  type IncidentOut,
} from "../api";

/** One page per breakage level (jrae 2026-08-20). Companies: credential-class
 *  breaks — a company quiet while its integration still delivers. Locations:
 *  per-location file breaks — a location quiet while its company delivers.
 *  Top: what's broken NOW at this level. Below: browse the level — pick an
 *  integration (and company, for locations) and walk the children live. */
export function LevelTab({
  level,
  onOpenIncident,
  onOpenEntity,
}: {
  level: "L2" | "L3";
  onOpenIncident: (id: string) => void;
  onOpenEntity: (level: string, keyPath: string) => void;
}) {
  const incidents = useIncidents({ state: "open", level, limit: 100, offset: 0 });
  const profiles = useProfiles();
  const wentQuiet = useNewEntities(45, "went_quiet");
  const [source, setSource] = useState<string | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  // CS feedback 2026-08-20: locations Cinch already marks inactive stopped on
  // purpose — keep them (annotated) but don't call them out by default.
  const [showInactive, setShowInactive] = useState(false);
  const inactiveCount = (wentQuiet.data ?? []).filter((r) => r.detail.inactive).length;
  const quietRows = (wentQuiet.data ?? []).filter(
    (r) => showInactive || !r.detail.inactive,
  );

  const sourceOptions: ComboOption[] = (profiles.data ?? [])
    .filter((p) => p.level === "L1")
    .map((p) => ({ id: p.key_path, label: p.key_path }));

  const companies = useChildren(level === "L3" && source ? "L1" : null, source);
  const companyOptions: ComboOption[] = (companies.data ?? []).map((c) => ({
    id: c.key_path,
    label: c.name ?? c.child_id,
  }));

  const browseParent = level === "L2" ? source : company;
  const browseLevel = level === "L2" ? "L1" : "L2";
  const children = useChildren(browseParent ? browseLevel : null, browseParent);

  const incidentColumns: DataTableColumn<IncidentOut>[] = [
    {
      key: "severity",
      header: "Severity",
      render: (r) => <StatusBadge value={severityBadgeValue(r)} />,
    },
    {
      key: "key_path",
      header: level === "L2" ? "Company" : "Location",
      render: (r) => (
        <EntityName
          name={level === "L2" ? r.company_name : r.location_name}
          fallback={r.key_path}
          context={`${r.entity_source}${
            level === "L3" && r.company_name ? ` · ${r.company_name}` : ""
          }`}
        />
      ),
    },
    {
      key: "trigger",
      header: "What broke",
      render: (r) => (
        <span className="text-xs">
          {r.trigger === "feed_stopped"
            ? level === "L2"
              ? "Stopped sending while the integration delivers — credential-class break"
              : "Stopped sending while the company delivers — location-file break"
            : r.trigger}
        </span>
      ),
    },
    {
      key: "streak_days",
      header: "Days quiet",
      numeric: true,
    },
    { key: "opened_on", header: "Since" },
    {
      key: "magnitude",
      header: "Normal /day",
      numeric: true,
      render: (r) => (r.magnitude != null ? Math.round(r.magnitude).toLocaleString() : "—"),
    },
    {
      key: "review_status",
      header: "Review",
      render: (r) => <StatusBadge value={r.review_status} />,
    },
  ];

  const quietColumns: DataTableColumn<FirstSeenOut>[] = [
    {
      key: "location_name",
      header: "Location",
      render: (r) => (
        <EntityName
          name={r.location_name}
          fallback={r.key_path}
          badge={
            r.detail.inactive ? (
              <StatusBadge value="inactive" label="Inactive in Cinch" />
            ) : null
          }
          context={
            <>
              {r.entity_source}
              {r.detail.entity_ref ? ` · ref ${r.detail.entity_ref}` : ""}
              {r.detail.entity_number && r.detail.entity_number !== r.detail.entity_ref
                ? ` · #${r.detail.entity_number}`
                : ""}
            </>
          }
        />
      ),
    },
    {
      key: "detail",
      header: "Rate (prior → recent)",
      render: (r) =>
        r.detail.prior_rate != null ? (
          <span className="text-destructive">
            {r.detail.prior_rate} → {r.detail.recent_rate}/day
          </span>
        ) : (
          "—"
        ),
    },
    { key: "detected_on", header: "Detected" },
  ];

  const childColumns: DataTableColumn<ChildRow>[] = [
    {
      key: "name",
      header: level === "L2" ? "Company" : "Location",
      render: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.name ?? r.child_id}</span>
          <span className="text-xs text-muted-foreground">{r.child_id}</span>
        </div>
      ),
    },
    {
      key: "volume_30d",
      header: "30d volume",
      numeric: true,
      render: (r) => r.volume_30d.toLocaleString(),
    },
    {
      key: "recent_rate",
      header: "Trend /day (prior → recent)",
      numeric: true,
      render: (r) =>
        r.prior_rate != null && r.recent_rate != null ? (
          <span className={r.recent_rate === 0 && r.prior_rate > 0 ? "text-destructive" : ""}>
            {r.prior_rate} → {r.recent_rate}
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Broken now at the {level === "L2" ? "company" : "location"} level
        </h2>
        <DataTable
          columns={incidentColumns}
          rows={incidents.data?.items ?? []}
          rowKey={(r) => r.id}
          loading={incidents.isLoading}
          onRowClick={(r) => onOpenIncident(r.id)}
          emptyMessage={
            level === "L2"
              ? "No companies are quiet while their integration delivers — no credential-class breaks detected."
              : "No locations are quiet while their company delivers — no location-file breaks detected."
          }
        />
      </div>

      {level === "L3" && (
        <div>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Went quiet in the last 45 days — churn, closure, or a break; a human decides
          </h2>
          <p className="mb-2 text-xs text-muted-foreground">
            Locations with a real prior run-rate that dropped to ~zero. This is the review
            ledger for EVERY feed; automatic location incidents are opt-in per feed in
            Admin (for per-location-file feeds). Click a row to see its chart.
            {inactiveCount > 0 && (
              <>
                {" "}
                <button
                  type="button"
                  className="underline underline-offset-2 hover:text-foreground"
                  onClick={() => setShowInactive((v) => !v)}
                >
                  {showInactive ? "Hide" : "Show"} {inactiveCount} already marked
                  inactive in Cinch
                </button>
              </>
            )}
          </p>
          <DataTable
            columns={quietColumns}
            rows={quietRows}
            rowKey={(r) => r.id}
            loading={wentQuiet.isLoading}
            onRowClick={(r) => onOpenEntity(r.level, r.key_path)}
            emptyMessage="No locations went quiet in the window."
            containerClassName="max-h-96"
          />
        </div>
      )}

      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Browse {level === "L2" ? "companies by integration" : "locations by company"}
        </h2>
        <div className="mb-2 flex flex-wrap gap-2">
          <div className="w-64">
            <Combobox
              options={sourceOptions}
              value={source}
              onChange={(v) => {
                setSource(v);
                setCompany(null);
              }}
              placeholder="Pick an integration"
              clearable
            />
          </div>
          {level === "L3" && source && (
            <div className="w-80">
              <Combobox
                options={companyOptions}
                value={company}
                onChange={setCompany}
                placeholder={companies.isLoading ? "Loading companies…" : "Pick a company"}
                clearable
              />
            </div>
          )}
        </div>
        <DataTable
          columns={childColumns}
          rows={children.data ?? []}
          rowKey={(r) => r.key_path}
          loading={children.isLoading}
          onRowClick={(r) => onOpenEntity(r.level, r.key_path)}
          emptyMessage={
            browseParent
              ? "No activity in the last 60 days."
              : level === "L2"
                ? "Pick an integration to list its companies."
                : "Pick an integration, then a company, to list its locations."
          }
          containerClassName="max-h-[32rem]"
        />
      </div>
    </div>
  );
}
